<template>
  <div v-if="!disabled" tabindex="-1" class="vcl-tooltip" v-bind="attrs" v-on='bodyHandlers' v-click-outside="toggleOff">
    <div v-if="!hasExternalTarget" ref="target" class="vcl-tooltip__target" v-on="handlers">
      <slot name="target">
        <VpIcon v-bind="{ icon: iconName, style: iconStyles }" />
      </slot>
    </div>
    <div ref="tooltip" :class="['vcl-tooltip__body', {'vcl-tooltip__body--custom': customFlag }]" v-bind="{ 'data-popper-placement': placement,'data-show': isVisible, 'data-static': sticky }">
      <div v-if="$slots.header" class="vcl-modal__header">
        <slot name="header" />
      </div>

      <div v-if="isVisible" class="vcl-tooltip__content" tabindex="-1" @focusout="toggleOff($event, true)">
        <slot>
          <p class="vcl-tooltip__body-inner">{{ body }}</p>
        </slot>
      </div>

      <div ref="arrow" class="vcl-tooltip__arrow" data-popper-arrow :style="arrowCSS"></div>
    </div>
  </div>
</template>

<script>
import { clickOutside } from '../../utilities/directives';

import { createPopper } from '@popperjs/core';
import VpIcon from '../vp-icon/VpIcon.vue';

export default {
  name: 'VpTooltip',
  components: { VpIcon },
  directives: {
    clickOutside,
  },
  props: {
    disabled: { type: Boolean, default: false },
    target: {
      type: Array,
      default: null,
      validator: (target) => target.length === 2,
    },
    body: { type: String, default: null },
    icon: { type: String, default: null },
    styles: { type: Object, default: () => ({}) },
    iconStyles: { type: [String, Object], default: null },
    customFlag: { type: Boolean, default: false },
    enableScrolling: { type: Boolean, default: false },
    delay: { type: Object, default: () => ({ show: '0', delay: '0' }) },
    openBy: {
      type: String,
      default: 'hover',
      validator: (value) => ['hover', 'click'].includes(value),
    },
    placement: { type: String, default: 'right' },
    fallbackPlacements: { type: Array, default: null },
    sticky: { type: Boolean, default: null },
    offset: {
      type: Array,
      default: () => [0, 15],
      /**
       * Popper Modifier.Offset [x, y]
       * x=(Skidding): offset along ref
       * y=(Distance): offset between ref
       */
    },
    padding: {
      type: Object,
      default: () => ({
        top: 10, right: 10, bottom: 0, left: 0,
      }),
    },
  },
  data: () => ({ 
    visible: false, 
    popperInstance: {}, 
    isWithinRange: false, 
  }),
  computed: {
    attrs() {
      const { style, ...attrs } = this.$attrs;
      return { attrs, style: this.bodyCSS };
    },
    hasExternalTarget() {
      if (!this.target) return false;

      const [refs, target] = this.target;
      return Boolean(refs[target]);
    },
    arrowCSS() {
      const arrow = this.styles.arrow || {};
      const { top, right, bottom, left } = arrow;
      const defaultBorder = '#dbdbdb';
      const defaultColor = '#fff';
      return {
        '--arrow-top-color': (top && top.color && top.color.length) ? top.color : defaultColor,
        '--arrow-top-border': (top && top.border && top.border.length) ? top.border : defaultBorder,
        '--arrow-right-color': (right && right.color && right.color.length) ? right.color : defaultColor,
        '--arrow-right-border': (right && right.border && right.border.length) ? right.border : defaultBorder,
        '--arrow-bottom-color': (bottom && bottom.color && bottom.color.length) ? bottom.color : defaultColor,
        '--arrow-bottom-border': (bottom && bottom.border && bottom.border.length) ? bottom.border : defaultBorder,
        '--arrow-left-color': (left && left.color && left.color.length) ? left.color : defaultColor,
        '--arrow-left-border': (left && left.border && left.border.length) ? left.border : defaultBorder,
      };
    },
    bodyCSS() {
      return {
        '--body-z-index': this.styles.zIndex || '1',
        '--body-border-radius': this.styles.borderRadius || '4px',
        '--body-content-border-radius': this.styles.contentBorderRadius || this.styles.borderRadius || '4px',
        '--body-content-overflow': this.enableScrolling ? 'auto' : 'unset',
        '--body-background': this.styles.background || '#fff',
        '--body-color': this.styles.color || '#4f4f4f',
        '--body-padding': this.styles.padding || '1rem',
        '--body-font-size': this.styles.fontSize || '1rem',
        '--root-width': this.styles.rootWidth || 'unset',
        '--body-width': this.styles.width || '187px',
        '--body-min-width': this.styles.minWidth || this.styles.width || 'unset',
        '--body-max-width': this.styles.maxWidth || this.styles.width || 'unset',
        '--root-height': this.styles.rootHeight || 'unset',
        '--body-height': this.styles.height || 'unset',
        '--body-min-height': this.styles.minHeight || 'unset',
        '--body-max-height': this.styles.maxHeight || 'unset',
      };
    },
    bodyHandlers() {
      const handlers = {
        blur: this.toggleOff,
        mouseenter: () => this.toggleRange(true),
        mouseleave: () => this.toggleRange(false),
      }
      if (this.openBy !== 'click') handlers.focus = this.toggleOn;
      return handlers;
    },  
    isVisible() {
      const { isWithinRange, visible, sticky } = this;
      return isWithinRange || sticky || visible;
    },
    convertedDelay() {
      return `${this.delay.show / 1000}s`;
    },
    Reference() {
      if (!this.hasExternalTarget) return this.$refs.target;
      const [refs, target] = this.target;
      const element = refs[target];
      return (element && element._isVue) ? element.$el : element;
    },
    Tooltip() {
      return this.$refs.tooltip;
    },
    Arrow() {
      return this.$refs.arrow;
    },
    handlers() {
      const handlers = {};
      if (this.openBy.toLowerCase() === 'hover') {
        handlers.mouseover = this.toggleOn;
        handlers.mouseleave = this.toggleOff;
      }
      if (this.openBy.toLowerCase() === 'click') {
        handlers.click = this.toggleOn;
      }
      return handlers;
    },
    iconName() {
      return (!this.icon || !this.icon.length) ? 'info' : this.icon;
    }
  },
  watch: {
    placement() {
      this.popperInstance.setOptions({ placement: this.placement });
    },
    visible(bool) {
      this.$emit('visible', bool);
    },
  },
  mounted() {
    if (!this.disabled) {
      if (this.hasExternalTarget) {
        return Promise.resolve(this.bindEvents())
          .then(() => this.setPopperInstance());
      }
      return this.setPopperInstance();
    }
    return null;
  },
  methods: {
    toggleRange(bool) {
      this.isWithinRange = this.openBy === 'click' ? false : bool;
    },
    bindEvents() {
      if (this.Reference) {
        Object.keys(this.handlers).forEach((key) => {
          this.Reference.addEventListener(key, this.handlers[key]);
        });
      }
    },
    setPopperInstance() {
      const opts = {
        placement: this.placement,
        modifiers: [
          { /** https://popper.js.org/docs/v2/modifiers/offset/ */
            name: 'offset',
            options: {
              offset: this.offset, // default [0,15]
              element: this.Arrow,
            },
          },
          { /** https://popper.js.org/docs/v2/modifiers/arrow/ */
            name: 'arrow',
            options: {
              element: this.Arrow,
              padding: this.padding,
            },
          },
        ],
      };
      if (this.fallbackPlacements) {
        opts.modifiers.push({ name: 'flip', options: { fallbackPlacements: this.fallbackPlacements } });
      }
      this.popperInstance = createPopper(this.Reference, this.Tooltip, opts);
    },
    toggleOn(event, _continue = false) {
      if (this.visible) {
        return (!this.sticky && event !== 'focus') ? this.toggleOff() : null;
      }
      
      /**
       * Todo: Use delay.show as setpoint,
       * if mouseover is active for delay.show amount of time, toggleOn()
      */

      // Make the tooltip visible
      this.visible = true;

      // Enable the event listeners
      this.setOptions({ name: 'eventListeners', enabled: true });

      // Update its position
      return this.popperInstance.update();
    },
    toggleOff($evt) {
      if (!this.visible) return null;
      if ($evt && $evt.currentTarget.contains($evt.relatedTarget)) return null;
      // Hide the tooltip
      this.visible = false;

      // Disable the event listeners
      return this.setOptions({ name: 'eventListeners', enabled: false });
    },
    setOptions(opts) {
      /**
       * Created setOptions method to patch bug found with popperInstance
       * popperInstance.setOptions for modifiers was overriding the entire options object
       * Meaning it wasn't keeping the original opts before mutations applied
       */
      let { modifiers } = this.popperInstance.state.options;
      let hasModified = false;
      // Updates Modifier if exists
      modifiers.forEach((mod, index) => {
        if (mod.name === opts.name) {
          modifiers[index] = opts;
          hasModified = true;
        }
      });
      // Appends Modifier if it wasn't found
      if (!hasModified) {
        modifiers = [...modifiers, opts];
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.vcl-tooltip {
  height: var(--root-height);
  width: var(--root-width);
}
.vcl-tooltip__body {
  display: none;
  z-index: var(--body-z-index);
  width: var(--body-width);
  min-width: var(--body-min-width);
  max-width: var(--body-max-width);
  height: var(--body-height);
  min-height: var(--body-min-height);
  max-height: var(--body-max-height);
  color: var(--body-color);
  font-size: .75rem;
  background: var(--body-background);
  border-radius: var(--body-border-radius);
  border: 1px solid #dbdbdb;
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.1);
  &[data-show], &[data-static] {
    display: flex !important;
    flex-direction: column;
  }
  & > * {
    flex: 1 1 100%;
  }
}

.vcl-tooltip__content {
  min-height: 0;
  overflow-y: var(--body-content-overflow);
  background: var(--body-background);
  border-radius: var(--body-content-border-radius);
}

.vcl-tooltip__body-inner {
  text-align: left;
  padding: var(--body-padding);
  font-size: var(--body-font-size);
}

div.vcl-tooltip__arrow {
  display: block;
  position: absolute;
  overflow: hidden;
  &:after {
    content: '';
    position: absolute;
    width: 12px;
    height: 12px;
    box-shadow:0 1px 8px 0px 1px 4px rgba(0, 0, 0, 0.1);
    left: 50%;
    transform: translateX(-50%) rotate(45deg);
    background-color: #fff;
    border: 1px solid #DBDBDB;
  }
}

.vcl-tooltip__body[data-popper-placement^='top'] {
  & > div.vcl-tooltip__arrow {
    width: 24px;
    height: 12px;
    bottom: -12px;
    &:after {
      bottom: 5px;
      background-color: var(--arrow-top-color);
      border: 1px solid var(--arrow-top-border);
    }
  }
}
.vcl-tooltip__body[data-popper-placement^='bottom'] {
  & > div.vcl-tooltip__arrow {
    top: -12px;
    width: 24px;
    height: 12px;
    &:after {
      top: 5px;
      background-color: var(--arrow-bottom-color);
      border: 1px solid var(--arrow-bottom-border);
    }
  }
}
.vcl-tooltip__body[data-popper-placement^='left'] {
  & > div.vcl-tooltip__arrow {
    right: -12px;
    width: 12px;
    height: 24px;
    &:after {
      left: 0;
      bottom: 5px;
      background-color: var(--arrow-left-color);
      border: 1px solid var(--arrow-left-border);
    }
  }
}
.vcl-tooltip__body[data-popper-placement^='right'] {
  & > div.vcl-tooltip__arrow {
    left: -12px;
    width: 12px;
    height: 24px;
    margin-bottom: -1px;
    &:after {
      left: 12px;
      bottom: 5px;
      background-color: var(--arrow-right-color);
      border: 1px solid var(--arrow-right-border);
    }
  }
}

</style>
